import { useState, useRef, useEffect, useMemo } from "react"
import { Play, Square, ChevronDown } from "lucide-react"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function VoiceSelector({ voices = [], value, onChange, disabled, label }) {
  const [playingId, setPlayingId] = useState(null)
  const [open, setOpen] = useState(false)
  const audioRef = useRef(null)

  const selectedVoice = voices.find((v) => v.id.toString() === value)

  const grouped = useMemo(() => {
    const male = voices.filter((v) => v.gender === "male")
    const female = voices.filter((v) => v.gender === "female")
    return [
      { label: "Female", voices: female },
      { label: "Male", voices: male },
    ].filter((g) => g.voices.length > 0)
  }, [voices])

  useEffect(() => {
    const audio = new Audio()
    audioRef.current = audio

    const handleEnded = () => setPlayingId(null)
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.removeEventListener("ended", handleEnded)
      audio.pause()
      audio.src = ""
    }
  }, [])

  const togglePlay = (e, voice) => {
    e.stopPropagation()
    e.preventDefault()
    const audio = audioRef.current
    if (!audio) return

    if (playingId === voice.id) {
      audio.pause()
      audio.currentTime = 0
      setPlayingId(null)
    } else {
      audio.src = voice.sampleUrl
      audio.play().catch(() => setPlayingId(null))
      setPlayingId(voice.id)
    }
  }

  const handleSelect = (voice) => {
    if (disabled) return
    onChange(voice.id.toString())
    setOpen(false)
  }

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${
              disabled
                ? "opacity-60 cursor-not-allowed bg-zinc-50"
                : "hover:border-zinc-400 cursor-pointer bg-white"
            } ${open ? "border-kf-red ring-1 ring-kf-red" : "border-zinc-200"}`}
          >
            {selectedVoice ? (
              <>
                {selectedVoice.imageUrl ? (
                  <img
                    src={selectedVoice.imageUrl}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-zinc-500">
                      {selectedVoice.name.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-900">
                    {selectedVoice.name}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {selectedVoice.descriptor}
                  </p>
                </div>
                {selectedVoice.sampleUrl && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => togglePlay(e, selectedVoice)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePlay(e, selectedVoice) } }}
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      playingId === selectedVoice.id
                        ? "bg-kf-red text-white"
                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                    }`}
                    aria-label={
                      playingId === selectedVoice.id
                        ? `Pause ${selectedVoice.name} preview`
                        : `Play ${selectedVoice.name} preview`
                    }
                  >
                    {playingId === selectedVoice.id ? (
                      <Square className="h-3 w-3" />
                    ) : (
                      <Play className="h-3 w-3 ml-0.5" />
                    )}
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm text-zinc-400">Select a voice...</span>
            )}
            <ChevronDown className="h-4 w-4 text-zinc-400 flex-shrink-0 ml-auto" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 max-h-80 overflow-y-auto"
          align="start"
        >
          {grouped.map((group) => (
            <div key={group.label}>
              <div className="px-3 py-1.5 text-xs font-semibold text-zinc-400 uppercase tracking-wider bg-zinc-50 sticky top-0">
                {group.label}
              </div>
              {group.voices.map((voice) => {
                const isSelected = value === voice.id.toString()
                const isPlaying = playingId === voice.id
                return (
                  <button
                    key={voice.id}
                    type="button"
                    onClick={() => handleSelect(voice)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                      isSelected
                        ? "bg-red-50"
                        : "hover:bg-zinc-50"
                    }`}
                  >
                    {voice.imageUrl ? (
                      <img
                        src={voice.imageUrl}
                        alt=""
                        className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-zinc-500">
                          {voice.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium ${isSelected ? "text-kf-red" : "text-zinc-900"}`}>
                        {voice.name}
                      </p>
                      {voice.descriptor && (
                        <p className="text-xs text-zinc-500 truncate">
                          {voice.descriptor}
                        </p>
                      )}
                    </div>
                    {voice.sampleUrl && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => togglePlay(e, voice)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); togglePlay(e, voice) } }}
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          isPlaying
                            ? "bg-kf-red text-white"
                            : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                        }`}
                        aria-label={
                          isPlaying
                            ? `Pause ${voice.name} preview`
                            : `Play ${voice.name} preview`
                        }
                      >
                        {isPlaying ? (
                          <Square className="h-3 w-3" />
                        ) : (
                          <Play className="h-3 w-3 ml-0.5" />
                        )}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  )
}
